import type { GameState, GameStats } from '@/types/game';

interface CommentaryEvent {
  id: string;
  message: string;
  duration?: number;
  priority?: 'low' | 'medium' | 'high';
  style?: 'normal' | 'excited' | 'warning' | 'achievement';
}

export class CommentarySystem {
  private activeCommentary: CommentaryEvent | null = null;
  private commentaryQueue: CommentaryEvent[] = [];
  private lastEventTime: number = 0;
  private callbacks: Set<(event: CommentaryEvent | null) => void> = new Set();
  
  constructor() {
    // Initialize commentary system
  }
  
  // Subscribe to commentary events
  public onCommentary(callback: (event: CommentaryEvent | null) => void): () => void {
    this.callbacks.add(callback);
    return () => {
      this.callbacks.delete(callback);
    };
  }
  
  // Notify all subscribers
  private notify(event: CommentaryEvent | null) {
    this.callbacks.forEach(cb => cb(event));
  }
  
  // Add commentary to queue
  private queueCommentary(event: CommentaryEvent) {
    // High priority messages jump to front
    if (event.priority === 'high') {
      this.commentaryQueue.unshift(event);
    } else {
      this.commentaryQueue.push(event);
    }
    this.processQueue();
  }
  
  // Process commentary queue
  private processQueue() {
    if (this.activeCommentary || this.commentaryQueue.length === 0) return;
    
    const event = this.commentaryQueue.shift()!;
    this.activeCommentary = event;
    this.notify(event);
    
    // Auto-clear after duration
    const duration = event.duration || 3000;
    setTimeout(() => {
      this.activeCommentary = null;
      this.notify(null);
      this.processQueue();
    }, duration);
  }
  
  // Analyze game state and trigger appropriate commentary
  public analyzeGameState(gameState: GameState, stats: GameStats) {
    const now = Date.now();
    
    // Debounce to avoid spam
    if (now - this.lastEventTime < 1000) return;
    
    // Level start commentary
    if (stats.stepsTaken === 0 && gameState.isPlaying) {
      this.triggerLevelStart(gameState.level);
      this.lastEventTime = now;
      return;
    }
    
    // Collision commentary
    if (gameState.hasCollided) {
      this.triggerCollision(stats);
      this.lastEventTime = now;
      return;
    }
    
    // Achievement milestones
    if (stats.stepsTaken > 0 && stats.stepsTaken % 50 === 0) {
      this.triggerMilestone(stats);
      this.lastEventTime = now;
      return;
    }
    
    // Near completion
    const distance = Math.abs(gameState.position[0] - gameState.end[0]) + 
                   Math.abs(gameState.position[1] - gameState.end[1]);
    if (distance <= 3 && distance > 0) {
      this.triggerNearCompletion();
      this.lastEventTime = now;
      return;
    }
    
    // Coin collection
    if (gameState.collectedInLevel > 0 && gameState.collectedInLevel % 5 === 0) {
      this.triggerCoinMilestone(gameState.collectedInLevel);
      this.lastEventTime = now;
      return;
    }
  }
  
  // Level start messages
  private triggerLevelStart(level: number) {
    const messages = [
      "Let's do this!",
      "Here we go again!",
      "Time for another challenge!",
      "You've got this!",
      "Show me what you've got!",
      "Ready when you are!",
      "New level, new adventure!",
      "We got this!",
      "Ready to roll!",
      "Let's make this one count!",
      "Time to embarrass yourself again!",
      "Another chance to walk into things!",
      "Let's see how creative your failures get!",
      "Ready to defy gravity... and logic?",
      "Your obstacles are waiting patiently!",
      "Time to turn left when you meant right!",
      "Another level, another opportunity for chaos!",
      "Let's see those legendary navigation skills!",
      "Your GPS has given up, but I believe in you!",
    ];
    
    if (level === 0) {
      this.queueCommentary({
        id: 'level-start-tutorial',
        message: "Welcome! Use arrow keys to reach the blue portal!",
        duration: 4000,
        priority: 'high',
        style: 'normal'
      });
    } else if (level % 10 === 0) {
      this.queueCommentary({
        id: `level-start-${level}`,
        message: `Level ${level + 1}! You're crushing it!`,
        duration: 3000,
        priority: 'medium',
        style: 'excited'
      });
    } else {
      this.queueCommentary({
        id: `level-start-${level}`,
        message: messages[Math.floor(Math.random() * messages.length)],
        duration: 2000,
        priority: 'low',
        style: 'normal'
      });
    }
  }
  
  // Collision messages
  private triggerCollision(stats: GameStats) {
    const totalCollisions = stats.rocksEncountered + stats.lavaEncountered + stats.stopsEncountered;
    
    if (stats.lavaEncountered > 0 && stats.lavaEncountered % 3 === 0) {
      const messages = [
        "Watch out for that lava!",
        "Lava is not your friend!",
        "Stop diving into the lava!",
        "That's hot! Literally!",
        "Lava surfing isn't a thing!",
        "You're on fire... literally!",
        "Maybe avoid the molten rock?",
        "That's some spicy terrain!",
        "Lava: 1, You: 0",
        "Swimming in lava is not recommended!",
        "Hot! Hot! Hot!",
        "The floor is literally lava!",
        "You're getting toasted!",
        "That's not a hot tub!",
        "Volcanic fashion sense, I see!",
        "Crispy player coming right up!",
        "Extra crispy mode: activated!",
        "Well done... literally!",
        "That's one way to warm up!",
        "Fire safety 101: Don't walk in lava!",
      ];
      this.queueCommentary({
        id: 'lava-warning',
        message: messages[Math.floor(Math.random() * messages.length)],
        duration: 3000,
        priority: 'medium',
        style: 'warning'
      });
    } else if (totalCollisions > 10) {
      const messages = [
        "Yo Chill!",
        "Bruh! You're hitting everything!",
        "You really like hitting things, don't you?",
        "Whoa! You're a magnet for obstacles!",
        "Are you trying to collect all the collisions?",
        "You must really like those walls!",
        "Collision course: activated!",
        "Like, it's not going anywhere dude!",
        "Aight, keep hitting it",
        "Maybe try going around instead?",
        "Those obstacles aren't going to move!",
        "Houston, we have a navigation problem!",
        "GPS says: 'Recalculating... forever'",
        "Did you mistake this for a demolition game?",
        "The walls are winning this battle!",
        "Obstacle course champion right here!",
        "You're really testing the collision physics!",
        "That's one way to explore the level!",
        "The scenic route involves fewer crashes!",
        "Maybe we need to work on those reflexes?",
        "Bumper cars mode: ACTIVATED!",
        "Are we speed running collisions now?",
        "Physics engine is getting a workout!",
        "Those obstacles have families too!",
      ];
      this.queueCommentary({
        id: 'collision-many',
        message: messages[Math.floor(Math.random() * messages.length)],
        duration: 2500,
        priority: 'low',
        style: 'warning'
      });
    } else {
      const messages = [
        "Bonk!",
        "Boop!",
        "Oops!",
        "Watch it!",
        "Careful now!",
        "Bump!",
        "Whoops!",
        "Easy there!",
        "Slow down!",
        "Mind the gap!",
        "That's gonna leave a mark!",
        "Thunk!",
        "Oof!",
        "Clumsy much?",
        "Nice collision!",
        "Smooth moves!",
        "Grace incarnate!",
        "10/10 landing!",
        "Nailed it!",
        "Physics lesson complete!",
        "The wall wins this round!",
        "Obstacle: 1, You: 0",
        "Try the door next time!",
        "That's not the way through!",
        "Solid as a rock... literally!",
      ];
      this.queueCommentary({
        id: 'collision-generic',
        message: messages[Math.floor(Math.random() * messages.length)],
        duration: 1500,
        priority: 'low',
        style: 'normal'
      });
    }
  }
  
  // Milestone messages
  private triggerMilestone(stats: GameStats) {
    if (stats.stepsTaken === 100) {
      this.queueCommentary({
        id: 'steps-100',
        message: "100 steps! You're a walking machine!",
        duration: 3000,
        priority: 'medium',
        style: 'achievement'
      });
    } else if (stats.stepsTaken === 500) {
      this.queueCommentary({
        id: 'steps-500',
        message: "500 steps! Marathon runner in the making!",
        duration: 3000,
        priority: 'medium',
        style: 'achievement'
      });
    }
  }
  
  // Near completion messages
  private triggerNearCompletion() {
    const messages = [
      "Almost there!",
      "You can see the finish!",
      "Just a few more steps!",
      "The end is in sight!",
      "Home stretch!",
      "So close I can taste it!",
      "Victory is within reach!",
      "Don't give up now!",
      "You're practically there!",
      "Final push time!",
      "The portal awaits!",
      "Sprint to the finish!",
      "You can smell victory!",
      "One final leap!",
      "The goal is right there!",
      "Finish strong!",
      "You've got this in the bag!",
      "Time to seal the deal!",
      "Touch that portal!",
      "Make it count!",
      "Don't trip at the finish line!",
      "Easy money from here!",
      "Victory lap incoming!",
      "Stick the landing!",
      "The stars are aligning!",
    ];
    
    this.queueCommentary({
      id: 'near-completion',
      message: messages[Math.floor(Math.random() * messages.length)],
      duration: 2000,
      priority: 'medium',
      style: 'excited'
    });
  }
  
  // Coin milestone messages
  private triggerCoinMilestone(coins: number) {
    const messages = [
      "Cha-ching!",
      "Coin collector extraordinaire!",
      "Money talks, and yours is saying 'more'!",
      "You're making it rain coins!",
      "Treasure hunter status: achieved!",
      "Your piggy bank is getting heavy!",
      "Coin magnet in action!",
      "Show me the money!",
      "Golden touch activated!",
      "You're on a coin streak!",
      "Riches await!",
      "Midas would be jealous!",
      "Your wallet is getting thick!",
      "Coin fever is real!",
      "You're swimming in gold!",
      "Ka-ching! Another one!",
      "Greed is good... for coins!",
      "Your pockets are jingling!",
      "Coin collector's paradise!",
      "Money doesn't grow on trees, but you're close!",
    ];

    if (coins === 5) {
      this.queueCommentary({
        id: 'coins-5',
        message: "First handful of coins!",
        duration: 2000,
        priority: 'low',
        style: 'achievement'
      });
    } else if (coins === 10) {
      this.queueCommentary({
        id: 'coins-10',
        message: "10 coins! You're rich!",
        duration: 2500,
        priority: 'low',
        style: 'achievement'
      });
    } else if (coins === 25) {
      this.queueCommentary({
        id: 'coins-25',
        message: "25 coins! Time to visit the shop!",
        duration: 3000,
        priority: 'medium',
        style: 'achievement'
      });
    } else if (coins === 50) {
      this.queueCommentary({
        id: 'coins-50',
        message: "50 coins! You're a treasure hunter!",
        duration: 3000,
        priority: 'medium',
        style: 'achievement'
      });
    } else if (coins % 20 === 0) {
      this.queueCommentary({
        id: `coins-${coins}`,
        message: messages[Math.floor(Math.random() * messages.length)],
        duration: 2000,
        priority: 'low',
        style: 'achievement'
      });
    }
  }
  
  // Trigger custom commentary
  public trigger(message: string, style: CommentaryEvent['style'] = 'normal', priority: CommentaryEvent['priority'] = 'medium') {
    this.queueCommentary({
      id: `custom-${Date.now()}`,
      message,
      duration: 3000,
      priority,
      style
    });
  }
  
  // Clear all commentary
  public clear() {
    this.commentaryQueue = [];
    if (this.activeCommentary) {
      this.activeCommentary = null;
      this.notify(null);
    }
  }
}

// Singleton instance
let commentarySystem: CommentarySystem | null = null;

export function getCommentarySystem(): CommentarySystem {
  if (!commentarySystem) {
    commentarySystem = new CommentarySystem();
  }
  return commentarySystem;
}